import { useEffect, useState } from "react";
import { getBranches } from "../services/branchService";

const CreateUserModal = ({
  open,
  mode = "create", // "create" | "edit"
  initialData = null,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    branchId: "",
    branchCode: "",
  });
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState({});
  const handleGetBranches = async () => {
    try {
      const response = await getBranches();
      if (response?.status === 200) {
        setBranches(response?.data);
      } else {
        console.error("Failed to fetch branches:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };
  /* ---------------------------------------
     FIX: RESET / PREFILL FORM CORRECTLY
  ---------------------------------------- */

  useEffect(() => {
    if (!open) {
      // clear form and errors when modal closes so previous validation messages don't persist
      setForm({
        name: "",
        email: "",
        password: "",
        branchId: "",
        branchCode: "",
      });
      setErrors({});
      return;
    }

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        email: initialData.email || "",
        password: "",
        branchId: initialData?.branch?.id || "",
        branchCode: initialData?.branch?.branchCode || "",
      });
    } else {
      // CREATE → clean slate
      setForm({
        name: "",
        email: "",
        password: "",
        branchId: "",
        branchCode: "",
      });
    }
  }, [open, mode, initialData]);

  useEffect(() => {
    handleGetBranches();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // clear field error on change
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };
  const handleBranchChange = (e) => {
    const selectedBranchId = e.target.value;

    const selectedBranch = branches?.find((b) => b._id === selectedBranchId);

    setForm((prev) => ({
      ...prev,
      branchId: selectedBranchId,
      branchCode: selectedBranch?.branchCode || "",
    }));

    setErrors((prev) => ({ ...prev, branchId: undefined }));
  };

  const validateForm = (values) => {
    const e = {};
    if (!values.name || !values.name.trim()) e.name = "Name is required";
    if (!values.email || !values.email.trim()) {
      e.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(values.email))
        e.email = "Enter a valid email address";
      // If you specifically want to enforce Gmail addresses, uncomment:
      // if (!values.email.endsWith("@gmail.com")) e.email = "Please use a Gmail address";
    }
    if (mode === "create" && (!values.password || !values.password.trim()))
      e.password = "Password is required";
    if (!values.branchId) e.branchId = "Please select a branch";
    return e;
  };

  const handleSubmit = () => {
    const payload = { ...form };

    // client-side validation
    const validation = validateForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    // ❌ Do not send password in edit mode
    if (mode === "edit") {
      delete payload.password;
      delete payload.email; // email should never be updated
    }

    onSubmit(payload);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-base font-semibold">
            {mode === "create" ? "Create User" : "Edit User"}
          </h2>
          <p className="text-xs text-gray-500">
            {mode === "create" ? "Add a new employee" : "Update user details"}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Name */}
          <Input
            label="Full Name"
            name="name"
            value={form.name}
            required
            onChange={handleChange}
          />
          {errors.name && (
            <p className="text-xs text-red-600 mt-1">{errors.name}</p>
          )}

          {/* Email */}
          <Input
            label="Email Address"
            name="email"
            type="email"
            required
            value={form.email}
            onChange={handleChange}
            disabled={mode === "edit"} // email locked on edit
          />
          {errors.email && (
            <p className="text-xs text-red-600">{errors.email}</p>
          )}

          {/* Password (only create) */}
          {mode === "create" && (
            <Input
              label="Temporary Password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
            />
          )}
          {errors.password && (
            <p className="text-xs text-red-600 mt-1">{errors.password}</p>
          )}

          <div>
            <label className="text-xs font-medium text-gray-600">Branch</label>
            <select
              name="branchId" // ✅ FIXED
              value={form.branchId} // ✅ FIXED
              onChange={handleBranchChange}
              className="w-full mt-1 px-3 py-2 border rounded-md text-sm
               focus:ring-2 focus:ring-sky-500 focus:outline-none"
            >
              <option value="">Select branch</option>
              {branches?.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.branchName}
                </option>
              ))}
            </select>
            {errors.branchId && (
              <p className="text-xs text-red-600 mt-1">{errors.branchId}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              !form.name ||
              !form.email ||
              (mode === "create" && !form.password) ||
              !form.branchId
            }
            className="px-4 py-2 bg-sky-600 text-white text-sm rounded-md
                       hover:bg-sky-700 disabled:opacity-50"
          >
            {isLoading
              ? "Loading..."
              : mode === "create"
                ? "Create User"
                : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- REUSABLE INPUT ---------------- */

const Input = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  disabled = false,
  required = false,
}) => (
  <div>
    <label className="text-xs font-medium text-gray-600">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={true}
      className="w-full mt-1 px-3 py-2 border rounded-md text-sm
                 focus:ring-2 focus:ring-sky-500 focus:outline-none
                 disabled:bg-gray-100 disabled:cursor-not-allowed"
    />
  </div>
);

export default CreateUserModal;
